import { useId } from "react";

import { Column, Row, Text } from "@/components";

import { cn } from "@/lib/utils";
import { PerformanceTier } from "@/types";

// Plot geometry, in the SVG's own 100 x 108 space: the lower lane below
// CHART_BOTTOM belongs to the Now / Projected readout.
const CHART_TOP = 8;
const CHART_BOTTOM = 88;
const CHART_VIEWBOX_HEIGHT = 108;
const FULL_WIDTH = 100;

// Past this multiple of goal the line is well clear of the card; capped only so
// the DOM never gets a silly number.
const MAX_GOAL_RATIO = 3;

const PROJECTION_COLOR: Record<PerformanceTier, string> = {
  [PerformanceTier.GREEN]: "#27AE60",
  [PerformanceTier.ORANGE]: "#E67E22",
  [PerformanceTier.RED]: "#D14343",
};

const PROJECTION_TEXT_CLASS: Record<PerformanceTier, string> = {
  [PerformanceTier.GREEN]: "text-emerald-600",
  [PerformanceTier.ORANGE]: "text-amber-600",
  [PerformanceTier.RED]: "text-red-600",
};

// Period-to-date as a solid line, the run-rate the rest of the way as a dashed
// one. Where they land against the goal is the whole point of the card.
export const PaceProjectionChart = ({
  current,
  formatValue,
  goal,
  progress,
  projected,
  tier,
}: {
  current: number;
  formatValue: (value: number) => string;
  goal: number;
  // Share of the period already banked, which is where the line elbows.
  progress: number;
  projected: number;
  tier: PerformanceTier;
}) => {
  /* React Hooks */
  // SVG ids are document-global, so this has to be per instance.
  const instanceId = useId().replace(/:/g, "");

  /* Helpers */
  const chartHeight = CHART_BOTTOM - CHART_TOP;
  const currentX = progress * FULL_WIDTH;
  const color = PROJECTION_COLOR[tier];
  const gradientId = `team-projection-${instanceId}`;

  // The axis is pinned to the goal, so the goal line always marks the goal and
  // a projection that beats it climbs above the card and gets clipped — being
  // on pace to crush it has to look different from just making it.
  const valueToY = (value: number) => {
    if (goal <= 0) return CHART_BOTTOM;
    const goalRatio = Math.min(value / goal, MAX_GOAL_RATIO);
    return CHART_BOTTOM - goalRatio * chartHeight;
  };
  const currentY = valueToY(current);
  const projectedY = valueToY(projected);
  const actualAreaPath = `M 0 ${CHART_VIEWBOX_HEIGHT.toString()} L 0 ${CHART_BOTTOM.toString()} L ${currentX.toString()} ${currentY.toString()} L ${currentX.toString()} ${CHART_VIEWBOX_HEIGHT.toString()} Z`;

  /* Render */
  return (
    <Column className="relative -mx-4 min-h-0 w-auto flex-1">
      <svg
        className="min-h-0 w-full flex-1 overflow-visible"
        preserveAspectRatio="none"
        viewBox={`0 0 ${FULL_WIDTH.toString()} ${CHART_VIEWBOX_HEIGHT.toString()}`}
      >
        <defs>
          <linearGradient id={gradientId} x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.3" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={actualAreaPath} fill={`url(#${gradientId})`} />
        <line
          stroke={color}
          strokeLinecap="round"
          strokeWidth="1"
          vectorEffect="non-scaling-stroke"
          x1="0"
          x2={currentX}
          y1={CHART_BOTTOM}
          y2={currentY}
        />
        <line
          stroke={color}
          strokeDasharray="4 3"
          strokeLinecap="round"
          strokeWidth="1"
          vectorEffect="non-scaling-stroke"
          x1={currentX}
          x2={FULL_WIDTH}
          y1={currentY}
          y2={projectedY}
        />
      </svg>
      <Column
        className="pointer-events-none absolute inset-x-0"
        style={{
          top: `${((CHART_TOP / CHART_VIEWBOX_HEIGHT) * 100).toString()}%`,
        }}
      >
        <Column className="absolute right-3 -translate-y-full items-end">
          <Text className="font-plex-mono text-[9px] font-medium uppercase tabular-nums tracking-wider text-slate-400">
            {formatValue(goal)}
          </Text>
        </Column>
        <Column className="h-px w-full bg-slate-200/70" />
      </Column>
      <Row className="absolute inset-x-2 bottom-1.5 items-end justify-between">
        <Column className="gap-0">
          <Text className="text-xs font-semibold tabular-nums text-slate-700">
            {formatValue(current)}
          </Text>
          <Text className="font-plex-mono text-[8px] font-medium uppercase tracking-wider text-slate-500">
            Now
          </Text>
        </Column>
        <Column className="items-end gap-0">
          <Text
            className={cn(
              "text-xs font-semibold tabular-nums",
              PROJECTION_TEXT_CLASS[tier],
            )}
          >
            {formatValue(projected)}
          </Text>
          <Text className="font-plex-mono text-[8px] font-medium uppercase tracking-wider text-slate-500">
            Projected
          </Text>
        </Column>
      </Row>
    </Column>
  );
};
